/**
 * Onboarding a joiner, in three steps.
 *
 * The book's rule for this flow is one sentence — "Three steps. Nothing is
 * created until the last one." — and it is the whole design. A form that
 * creates the employee and then fails to send the invite has already made a
 * record somebody has to clean up; a form that creates nothing until the last
 * click can be abandoned at any point with no trace.
 *
 * The third step is a review rather than a confirmation. It states what the
 * checklist will issue and which step falls due first, because the joining date
 * drives every due date and that consequence is invisible while you are picking
 * a date.
 */

const DAY_MS = 86400000;

export const STEPS = [
  { key: "personal", label: "Personal details" },
  { key: "job", label: "Job details" },
  { key: "review", label: "Review" },
];

/** Which fields each step owns, so validation asks only about what is shown. */
export const STEP_FIELDS = {
  personal: ["fname", "lname", "email", "phone"],
  job: ["designation", "department", "reportingTo", "location", "employmentType", "onboardingType", "joiningDate"],
  review: [],
};

/** The fields that must be filled before the flow can finish. */
export const REQUIRED = ["fname", "lname", "email", "designation", "department", "location", "joiningDate"];

const isBlank = (v) => v == null || String(v).trim() === "";

/**
 * What is missing on one step.
 *
 * Only the step's own fields: telling someone on step one that a joining date
 * is missing points at a field they cannot see.
 */
export const stepErrors = (form = {}, step) => {
  const fields = STEP_FIELDS[step] || [];
  const out = {};
  fields.forEach((f) => {
    if (REQUIRED.includes(f) && isBlank(form[f])) out[f] = "This is needed.";
  });
  if (fields.includes("email") && !isBlank(form.email) && !/^\S+@\S+\.\S+$/.test(String(form.email))) {
    out.email = "That does not look like an email address.";
  }
  return out;
};

export const canContinue = (form, step) => Object.keys(stepErrors(form, step)).length === 0;

/** Every field the whole flow still needs, for the review step to report. */
export const missingForSubmit = (form = {}) => REQUIRED.filter((f) => isBlank(form[f]));

export const stepIndex = (step) => Math.max(0, STEPS.findIndex((s) => s.key === step));
export const nextStep = (step) => STEPS[Math.min(stepIndex(step) + 1, STEPS.length - 1)].key;
export const prevStep = (step) => STEPS[Math.max(stepIndex(step) - 1, 0)].key;
export const isLast = (step) => stepIndex(step) === STEPS.length - 1;

/**
 * The template that will be issued.
 *
 * `appliesTo` narrows a template to a kind of hire; an empty list means "any".
 * A named match wins over the catch-all, so an organisation with a contractor
 * template gets it without having to make it the default.
 */
export const templateFor = (templates = [], onboardingType) => {
  const list = (templates || []).filter((t) => !t.isDeleted);
  if (!list.length) return null;
  if (onboardingType) {
    const named =
      list.find((t) => String(t._id) === String(onboardingType)) ||
      list.find((t) => (t.appliesTo || []).includes(onboardingType));
    if (named) return named;
  }
  return list.find((t) => t.isDefault) || list.find((t) => !(t.appliesTo || []).length) || list[0];
};

const utcDay = (value) => {
  if (!value) return null;
  const s = String(value);
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m) return Date.parse(`${m[1]}-${m[2]}-${m[3]}T00:00:00.000Z`);
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

/** "1 April" — spelled from a table; ICU renders "Sept" for September. */
export const longDate = (value) => {
  const t = utcDay(value);
  if (t == null) return null;
  const d = new Date(t);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
};

/**
 * What the checklist will look like once it exists.
 *
 * Due dates are the joining date plus each step's offset — the same arithmetic
 * the server does when it copies the template, so the review does not promise
 * a date the checklist will not have.
 */
export const previewChecklist = (template, joiningDate) => {
  const items = (template?.items || []).filter((i) => i);
  if (!items.length) return { name: template?.name || null, count: 0, first: null, items: [] };

  const base = utcDay(joiningDate);
  const dated = items
    .map((i) => ({
      label: i.label,
      owner: i.owner,
      required: i.required !== false,
      offset: Number(i.dueOffsetDays) || 0,
      due: base == null ? null : new Date(base + (Number(i.dueOffsetDays) || 0) * DAY_MS).toISOString().slice(0, 10),
      order: i.order ?? 0,
    }))
    .sort((a, b) => a.offset - b.offset || a.order - b.order);

  return {
    name: template?.name || null,
    count: dated.length,
    requiredCount: dated.filter((i) => i.required).length,
    // The step that falls due first is the one the joiner or HR hits on day one.
    first: dated[0] || null,
    items: dated,
  };
};

export const OWNER_LABEL = { employee: "the joiner", admin: "HR", it: "IT", manager: "their manager" };

/** The review's one-line summary of what the first owed step is. */
export const firstOwedLine = (preview) => {
  if (!preview?.first) return "This template has no steps.";
  const who = OWNER_LABEL[preview.first.owner] || preview.first.owner;
  const when = preview.first.due ? longDate(preview.first.due) : null;
  return when
    ? `${preview.first.label} — owed by ${who}, due ${when}`
    : `${preview.first.label} — owed by ${who}`;
};
